<template>
    <Teleport to="body">
        <Transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0"
            leave-active-class="transition ease-in duration-150"
            leave-to-class="opacity-0"
        >
            <div v-if="show" class="fixed inset-0 z-40 bg-gray-900/50" @click="$emit('close')" />
        </Transition>

        <Transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="translate-y-8 opacity-0"
            leave-active-class="transition ease-in duration-150"
            leave-to-class="translate-y-8 opacity-0"
        >
            <div
                v-if="show && space"
                class="fixed inset-x-0 bottom-0 z-50 max-h-[90vh] overflow-y-auto bg-white rounded-t-2xl px-4 pt-2 pb-4"
                role="dialog"
                aria-modal="true"
                aria-label="Confirm your booking"
            >
                <div class="w-9 h-1 mx-auto mb-3 rounded-full bg-gray-200" />

                <div class="flex items-start justify-between gap-3">
                    <div>
                        <h2 class="text-lg font-bold tracking-tight">{{ space.name }}</h2>
                        <p class="text-sm text-gray-600">{{ startLabel }}</p>
                    </div>
                    <button
                        type="button"
                        class="shrink-0 flex items-center justify-center w-11 h-11 rounded-full bg-gray-100 transition hover:bg-gray-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                        aria-label="Close"
                        @click="$emit('close')"
                    >
                        <i class="fa-solid fa-xmark text-gray-700" aria-hidden="true" />
                    </button>
                </div>

                <p id="booking-length" class="mt-3.5 text-sm font-bold uppercase tracking-wider text-gray-600">How long?</p>

                <div class="flex gap-2 mt-2" role="group" aria-labelledby="booking-length">
                    <button
                        v-for="preset in presets"
                        :key="preset.hours"
                        type="button"
                        class="grow h-14 flex flex-col items-center justify-center rounded-[0.625rem] border-[1.5px] transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                        :class="preset.hours === hours ? 'bg-primary-50 border-primary' : 'bg-white border-gray-200'"
                        :aria-pressed="preset.hours === hours"
                        @click="$emit('update:hours', preset.hours)"
                    >
                        <span
                            class="text-base font-bold"
                            :class="preset.hours === hours ? 'text-primary-900' : 'text-gray-900'"
                        >
                            {{ preset.hours }}h
                        </span>
                        <span
                            class="text-sm font-semibold"
                            :class="preset.hours === hours ? 'text-primary-800' : 'text-gray-600'"
                        >
                            {{ preset.price === false ? "No rate" : `£${preset.price}` }}
                        </span>
                    </button>
                </div>

                <div class="flex items-center justify-between mt-3 p-2.5 bg-gray-50 border border-gray-200 rounded-[0.625rem]">
                    <button
                        type="button"
                        class="flex items-center justify-center w-11 h-11 rounded-lg transition"
                        :class="canDecrease ? 'bg-white text-primary-800' : 'bg-gray-100 text-gray-500 cursor-not-allowed'"
                        :disabled="!canDecrease"
                        aria-label="One hour less"
                        @click="$emit('update:hours', hours - 1)"
                    >
                        <i class="fa-solid fa-minus" aria-hidden="true" />
                    </button>
                    <div class="text-center">
                        <div class="text-base font-bold">{{ rangeLabel }}</div>
                        <div class="text-sm text-gray-600">{{ limitLabel }}</div>
                    </div>
                    <button
                        type="button"
                        class="flex items-center justify-center w-11 h-11 rounded-lg transition"
                        :class="canIncrease ? 'bg-white text-primary-800' : 'bg-gray-100 text-gray-500 cursor-not-allowed'"
                        :disabled="!canIncrease"
                        aria-label="One hour more"
                        @click="$emit('update:hours', hours + 1)"
                    >
                        <i class="fa-solid fa-plus" aria-hidden="true" />
                    </button>
                </div>

                <template v-if="price !== false">
                    <div class="flex items-baseline justify-between mt-3.5">
                        <span class="text-sm text-gray-600">{{ hoursLabel }} · inc. VAT</span>
                        <span class="text-2xl font-bold tracking-tight">£{{ price }}</span>
                    </div>
                    <button
                        type="button"
                        class="w-full h-14 mt-3 flex items-center justify-center rounded-[0.625rem] bg-primary text-sm font-bold uppercase tracking-widest text-white transition hover:bg-tertiary hover:text-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                        @click="$emit('book')"
                    >
                        Book · £{{ price }}
                    </button>
                </template>

                <template v-else>
                    <div class="flex items-start gap-2 mt-3.5 p-3 bg-secondary-50 border border-secondary-200 rounded-[0.625rem]">
                        <i class="fa-solid fa-circle-exclamation text-secondary-700 mt-0.5" aria-hidden="true" />
                        <p class="text-sm text-secondary-900">
                            We don't have a rate for {{ hoursLabel }} on this table. Try a different length.
                        </p>
                    </div>
                    <div
                        class="w-full h-14 mt-3 flex items-center justify-center rounded-[0.625rem] bg-gray-200 text-sm font-bold uppercase tracking-widest text-gray-600"
                    >
                        Book
                    </div>
                </template>

                <p v-if="minHours > 1" class="mt-2.5 text-center text-sm text-gray-600">
                    Minimum {{ minHours }} hours on this table
                </p>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
    import { computed, onBeforeUnmount, onMounted } from "vue";
    import { formatHourLong } from "../../Composables/useBookingSchedule.js";

    const props = defineProps({
        show: Boolean,
        space: Object,
        currentDay: Object,
        startTime: Number,
        hours: Number,
        minHours: {
            type: Number,
            default: 1,
        },
        maxHours: {
            type: Number,
            default: 1,
        },
        /** Home.vue owns the bracket lookup; the sheet only asks it what a length costs. */
        priceFor: Function,
    });

    const emits = defineEmits(["close", "update:hours", "book"]);

    // A sheet with aria-modal has to answer Escape, or keyboard users are stuck in it.
    const closeOnEscape = (event) => {
        if (event.key === "Escape" && props.show) {
            emits("close");
        }
    };

    onMounted(() => window.addEventListener("keydown", closeOnEscape));

    onBeforeUnmount(() => window.removeEventListener("keydown", closeOnEscape));

    const price = computed(() => props.priceFor?.(props.hours) ?? false);

    const canDecrease = computed(() => props.hours > props.minHours);
    const canIncrease = computed(() => props.hours < props.maxHours);

    const startLabel = computed(() =>
        props.currentDay && props.startTime !== null
            ? `${props.currentDay.toFormat("ccc d LLL")} · from ${formatHourLong(props.startTime)}`
            : "",
    );

    const rangeLabel = computed(() =>
        props.startTime === null
            ? ""
            : `${formatHourLong(props.startTime)} – ${formatHourLong(props.startTime + props.hours)}`,
    );

    const hoursLabel = computed(() => `${props.hours} hour${props.hours === 1 ? "" : "s"}`);

    const limitLabel = computed(() =>
        props.hours >= props.maxHours
            ? "Longest slot available here"
            : `Up to ${props.maxHours} hour${props.maxHours === 1 ? "" : "s"} available`,
    );

    // The three shortest lengths that fit, so the common choice is one tap.
    const presets = computed(() => {
        const options = [];

        for (let length = props.minHours; length <= props.maxHours && options.length < 3; length++) {
            options.push({ hours: length, price: props.priceFor?.(length) ?? false });
        }

        return options;
    });
</script>
